import {
  AlertTriangle,
  Bot,
  Clock,
  FileText,
  Terminal,
  Users,
} from "lucide-react";
import { ErrorLogPanel } from "../../ui/ErrorLogPanel";
import { TeamPanel } from "../../team/TeamPanel";
import { HarperOpsPanel } from "../../harper-ops/HarperOpsPanel";
import { FooterUpcomingScoutsPanel } from "../../scouts/FooterUpcomingScoutsPanel";
import {
  CURRENT_VERSION,
  RELEASE_FEATURE_BRIEF,
} from "../../onboarding/tour-content";
import {
  FOOTER_BODY_FONT_STYLE,
  FOOTER_DATA_FONT_STYLE,
  FOOTER_HEADING_FONT_STYLE,
  type FooterPanelTab,
} from "./footer-toolbar-model";
import type { FooterTerminalController } from "./use-footer-terminal";
import { FooterTerminalPanel } from "./FooterTerminalPanel";

interface FooterDrawerProps {
  activeTab: FooterPanelTab;
  errorCount: number;
  open: boolean;
  setActiveTab: (tab: FooterPanelTab) => void;
  terminal: FooterTerminalController;
}

const TABS = [
  { id: "terminal", label: "Terminal", Icon: Terminal },
  { id: "scouts", label: "Scouts", Icon: Clock },
  { id: "changelog", label: "Changelog", Icon: FileText },
  { id: "errors", label: "Errors", Icon: AlertTriangle },
  { id: "team", label: "Team", Icon: Users },
  { id: "harper-ops", label: "Harper Ops", Icon: Bot },
] as const;

export function FooterDrawer(props: FooterDrawerProps) {
  return (
    <div
      className="fintheon-footer-drawer overflow-hidden transition-all duration-300 ease-in-out"
      style={{ maxHeight: props.open ? "280px" : "0px" }}
    >
      <div className="flex h-[280px] flex-col border-b border-[var(--fintheon-accent)]/10">
        <div className="flex shrink-0 items-center gap-0 border-b border-[var(--fintheon-accent)]/10 bg-transparent">
          {TABS.map(({ id, label, Icon }) => {
            const selected = props.activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => props.setActiveTab(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                  selected
                    ? id === "errors"
                      ? "text-[var(--fintheon-severe)]"
                      : "text-[var(--fintheon-accent)]"
                    : "text-[var(--fintheon-muted)] hover:text-[var(--fintheon-text)]"
                }`}
                style={FOOTER_HEADING_FONT_STYLE}
                aria-pressed={selected}
              >
                <Icon className="h-3 w-3" />
                {label}
                {id === "errors" && props.errorCount > 0 ? (
                  <span
                    className="ml-1 text-[8px] leading-none text-[var(--fintheon-severe)]"
                    style={FOOTER_DATA_FONT_STYLE}
                  >
                    {props.errorCount}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          style={FOOTER_BODY_FONT_STYLE}
        >
          {props.activeTab === "terminal" ? (
            <FooterTerminalPanel terminal={props.terminal} />
          ) : null}
          {props.activeTab === "changelog" ? <FooterChangelog /> : null}
          {props.activeTab === "errors" ? <ErrorLogPanel /> : null}
          {props.activeTab === "scouts" ? <FooterUpcomingScoutsPanel /> : null}
          {props.activeTab === "team" ? <TeamPanel /> : null}
          {props.activeTab === "harper-ops" ? <HarperOpsPanel /> : null}
        </div>
      </div>
    </div>
  );
}

function FooterChangelog() {
  return (
    <div className="space-y-2 px-3 py-2">
      {RELEASE_FEATURE_BRIEF.items.map((entry) => (
        <div
          key={`${entry.category}-${entry.title}`}
          className="flex gap-3 text-[11px]"
        >
          <span
            className="w-[88px] shrink-0 text-[var(--fintheon-accent)]/40"
            style={FOOTER_DATA_FONT_STYLE}
          >
            Epoch {CURRENT_VERSION}
          </span>
          <span
            className="w-[122px] shrink-0 text-[10px] text-[var(--fintheon-muted)]"
            style={FOOTER_HEADING_FONT_STYLE}
          >
            {entry.category}
          </span>
          <span className="flex-1 text-[var(--fintheon-text)]/60">
            <strong className="font-semibold text-[var(--fintheon-text)]/78">
              {entry.title}
            </strong>{" "}
            {entry.body}
          </span>
        </div>
      ))}
    </div>
  );
}
